import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { Link } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FileImage, FileText, Loader2, CheckCircle2, Clock,
  AlertCircle, Cpu, ShieldCheck, ShieldAlert, Trash2, Filter, Tag, Link2,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DocumentDetailDialog } from "./DocumentDetailDialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu, FilterCategory } from "@/components/ui/add-filter-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { Button } from "@/components/ui/button";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { DocumentCard } from "./DocumentCard";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { toast } from "sonner";

interface ExtractionRow {
  id: string;
  file_name: string;
  file_path: string;
  status: string;
  document_type: string;
  extracted_data: Record<string, any> | null;
  verified_data: Record<string, any> | null;
  is_verified: boolean;
  error_message: string | null;
  uploaded_by_email: string | null;
  created_at: string;
  processed_at: string | null;
  verified_by_email: string | null;
  verified_at: string | null;
}

export type { ExtractionRow };

function StatusBadge({ status, isVerified, isAttached, attachedOrderId }: { status: string; isVerified?: boolean; isAttached?: boolean; attachedOrderId?: string | null }) {
  const config: Record<string, { icon: React.ElementType; label: string; className: string }> = {
    pending: { icon: Clock, label: "Pending", className: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800" },
    processing: { icon: Cpu, label: "Processing", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800" },
    in_progress: { icon: Cpu, label: "In Progress", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800" },
    attached: { icon: Link2, label: "Attached", className: "bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30" },
    completed: { icon: CheckCircle2, label: "Processed", className: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800" },
    failed: { icon: AlertCircle, label: "Error", className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800" },
  };

  let resolved = status;
  if (isAttached) {
    resolved = "attached";
  } else if (status === "completed") {
    resolved = isVerified ? "completed" : "in_progress";
  }

  const c = config[resolved] || config.pending;
  const Icon = c.icon;

  if (isAttached && attachedOrderId) {
    return (
      <Link
        to={`/orders/${attachedOrderId}`}
        onClick={(e) => e.stopPropagation()}
        className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap hover:opacity-80 transition-opacity", c.className)}
      >
        <Icon className="h-3 w-3 shrink-0" />
        {attachedOrderId}
      </Link>
    );
  }

  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap", c.className)}>
      <Icon className="h-3 w-3 shrink-0" />
      {c.label}
    </span>
  );
}

export { StatusBadge };

export function DocumentHistory() {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ExtractionRow | null>(null);
  const [verifiedFilter, setVerifiedFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string[]>(["all"]);
  const [typeFilter, setTypeFilter] = useState<string[]>(["all"]);
  const queryClient = useQueryClient();

  const { data: extractions, isLoading } = useQuery({
    queryKey: ["document-extractions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_extractions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as ExtractionRow[];
    },
  });

  // Check which documents are attached to orders
  const { data: attachedMap } = useQuery({
    queryKey: ["attached-document-paths"],
    queryFn: async () => {
      const [bolRes, tenderRes] = await Promise.all([
        supabase.from("order_bol_images").select("image_path, order_id, orders!order_bol_images_order_id_fkey(unique_id)"),
        supabase.from("order_tender_images").select("image_path, order_id, orders!order_tender_images_order_id_fkey(unique_id)"),
      ]);
      const map = new Map<string, string>();
      (bolRes.data || []).forEach((r: any) => {
        if (r.image_path) {
          const orderId = r.orders?.unique_id || r.order_id;
          map.set(r.image_path, orderId);
        }
      });
      (tenderRes.data || []).forEach((r: any) => {
        if (r.image_path) {
          const orderId = r.orders?.unique_id || r.order_id;
          map.set(r.image_path, orderId);
        }
      });
      return map;
    },
  });

  const isDocAttached = (row: ExtractionRow): boolean => {
    if (!attachedMap) return false;
    if (attachedMap.has(row.file_path)) return true;
    for (const p of attachedMap.keys()) {
      if (p && row.file_name && p.includes(row.file_name)) return true;
    }
    return false;
  };

  const getAttachedOrderId = (row: ExtractionRow): string | null => {
    if (!attachedMap) return null;
    if (attachedMap.has(row.file_path)) return attachedMap.get(row.file_path)!;
    for (const [p, orderId] of attachedMap.entries()) {
      if (p && row.file_name && p.includes(row.file_name)) return orderId;
    }
    return null;
  };

  const getResolvedStatus = (row: ExtractionRow): string => {
    if (isDocAttached(row)) return "attached";
    if (row.status === "completed") return row.is_verified ? "completed" : "in_progress";
    return row.status;
  };

  const getDocType = (row: ExtractionRow): string => {
    if (row.document_type === "tender") return "tender";
    if (row.document_type === "bol") return "bol";
    return "other";
  };

  const filtered = (extractions || []).filter((row) => {
    if (verifiedFilter === "verified" && !row.is_verified) return false;
    if (verifiedFilter === "needs_verification" && (row.is_verified || row.status !== "completed")) return false;

    const activeStatuses = statusFilter.filter((v) => v && v !== "all");
    if (activeStatuses.length > 0) {
      const resolved = getResolvedStatus(row);
      if (!activeStatuses.includes(resolved)) return false;
    }

    const activeTypes = typeFilter.filter((v) => v && v !== "all");
    if (activeTypes.length > 0) {
      const docType = getDocType(row);
      if (!activeTypes.includes(docType)) return false;
    }

    if (search) {
      const q = search.toLowerCase();
      const d = row.verified_data || row.extracted_data || {};
      return (
        row.file_name.toLowerCase().includes(q) ||
        (row.uploaded_by_email ?? "").toLowerCase().includes(q) ||
        (d.bol_number ?? "").toLowerCase().includes(q) ||
        (d.order_number ?? "").toLowerCase().includes(q) ||
        (d.shipper_name ?? "").toLowerCase().includes(q) ||
        (d.consignee_name ?? "").toLowerCase().includes(q) ||
        (d.carrier_name ?? "").toLowerCase().includes(q) ||
        row.document_type.toLowerCase().includes(q)
      );
    }
    return true;
  });


  const {
    page, setPage, pageSize, setPageSize, totalPages, totalCount, pageRows,
  } = useTablePagination(filtered);

  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(filtered);

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("document_extractions").delete().in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: (deleteCount) => {
      queryClient.invalidateQueries({ queryKey: ["document-extractions"] });
      toast.success(`Deleted ${deleteCount} document(s)`);
      clear();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const getDisplayData = (row: ExtractionRow) => row.verified_data || row.extracted_data || {};

  const getPrimaryId = (row: ExtractionRow) => {
    const d = getDisplayData(row);
    return row.document_type === "tender" ? d.order_number : d.bol_number;
  };

  const getCarrier = (row: ExtractionRow) => {
    const d = getDisplayData(row);
    return d.carrier_name || null;
  };

  return (
    <div className="space-y-4">
      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-1">
        <DocumentChipFilters
          verifiedFilter={verifiedFilter}
          onVerifiedFilterChange={setVerifiedFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
        />
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {filtered.length} document{filtered.length !== 1 ? "s" : ""}
          </span>
          <ExpandingSearch value={search} onChange={setSearch} placeholder="Search by BOL #, carrier, filename..." />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FileImage className="h-12 w-12 mb-3 opacity-40" />
          <p>No documents found</p>
        </div>
      ) : (
        <>
          <div>
            <BulkActionBar count={count} onClear={clear} entityLabel="document">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
                onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                disabled={bulkDeleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </BulkActionBar>
            <div className={`border rounded-lg ${count > 0 ? "rounded-t-none border-t-0" : ""}`}>
              <ResponsiveTableShell
                cards={
                  <div className="flex flex-col gap-3 p-3">
                    {pageRows.map((row) => (
                      <DocumentCard
                        key={row.id}
                        fileName={row.file_name}
                        documentType={row.document_type}
                        createdAt={row.created_at}
                        uploadedByEmail={row.uploaded_by_email}
                        primaryId={getPrimaryId(row)}
                        carrier={getCarrier(row)}
                        isVerified={!!row.is_verified}
                        status={row.status}
                        badge={
                          <StatusBadge
                            status={row.status}
                            isVerified={row.is_verified}
                            isAttached={isDocAttached(row)}
                            attachedOrderId={getAttachedOrderId(row)}
                          />
                        }
                        onClick={() => setSelected(row)}
                      />
                    ))}
                  </div>
                }
                table={
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Select all"
                        {...(someSelected ? { "data-state": "indeterminate" } : {})}
                      />
                    </TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dashed border-muted-foreground/50">Status</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs max-w-[280px]">The processing status of the document: Pending, Processing, Processed, or Attached to an order.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableHead>
                    <TableHead>ID / Number</TableHead>
                    <TableHead>Carrier</TableHead>
                    <TableHead>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dashed border-muted-foreground/50">Verification</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs max-w-[280px]">Indicates whether extracted data has been reviewed and confirmed by a user or auto-verified with high confidence.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableHead>
                    <TableHead>Uploaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((row) => (
                    <TableRow key={row.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelected(row)}>
                      <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          onCheckedChange={() => toggle(row.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          className="font-medium max-w-[180px] truncate text-primary hover:underline"
                          onClick={(e) => { e.stopPropagation(); setSelected(row); }}
                        >
                          {row.file_name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {row.document_type === "tender" ? "Load Tender" : "Bill of Lading"}
                        </span>
                      </TableCell>
                      <TableCell><StatusBadge status={row.status} isVerified={row.is_verified} isAttached={isDocAttached(row)} attachedOrderId={getAttachedOrderId(row)} /></TableCell>
                      <TableCell className="text-muted-foreground">{getPrimaryId(row) ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{getCarrier(row) ?? "—"}</TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                {row.is_verified ? (
                                  <>
                                    <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                                    <span className="text-xs text-primary font-medium">Verified</span>
                                  </>
                                ) : row.status === "completed" ? (
                                  <>
                                    <ShieldAlert className="h-4 w-4 shrink-0 text-amber-500" />
                                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Needs verification</span>
                                  </>
                                ) : null}
                              </span>
                            </TooltipTrigger>
                            {(row.is_verified || row.status === "completed") && (
                              <TooltipContent>
                                {row.is_verified ? (
                                  <div className="text-xs">
                                    <p className="font-medium">Verified</p>
                                    {row.verified_by_email && <p>By: {row.verified_by_email}</p>}
                                    {row.verified_at && <p>{format(new Date(row.verified_at), "MMM d, yyyy h:mm a")}</p>}
                                    {!row.verified_by_email && !row.verified_at && <p>Auto-verified (high confidence)</p>}
                                  </div>
                                ) : (
                                  <p className="text-xs">Needs verification</p>
                                )}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        <div>{row.uploaded_by_email ?? "—"}</div>
                        <div className="text-xs whitespace-nowrap">{format(new Date(row.created_at), "MMM d, yyyy h:mm a")}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                }
              />
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </div>
        </>
      )}

      <DocumentDetailDialog row={selected} isAttached={selected ? isDocAttached(selected) : false} attachedOrderId={selected ? getAttachedOrderId(selected) : null} onClose={() => setSelected(null)} />
    </div>
  );
}

const VERIFIED_OPTIONS: ChipFilterOption[] = [
  { value: "verified", label: "Verified" },
  { value: "needs_verification", label: "Needs Verification" },
];

const DOC_STATUS_OPTIONS: ChipFilterOption[] = [
  { value: "attached", label: "Attached" },
  { value: "in_progress", label: "In Progress" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Processed" },
];

const DOC_TYPE_OPTIONS: ChipFilterOption[] = [
  { value: "bol", label: "Bill of Lading" },
  { value: "tender", label: "Load Tender" },
  { value: "other", label: "Other" },
];

type DocFilterKey = "verified" | "status" | "type";

function DocumentChipFilters({
  verifiedFilter,
  onVerifiedFilterChange,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
}: {
  verifiedFilter: string;
  onVerifiedFilterChange: (v: string) => void;
  statusFilter: string[];
  onStatusFilterChange: (v: string[]) => void;
  typeFilter: string[];
  onTypeFilterChange: (v: string[]) => void;
}) {
  const [activeFilters, setActiveFilters] = useState<DocFilterKey[]>(() => {
    const initial: DocFilterKey[] = [];
    if (verifiedFilter !== "all") initial.push("verified");
    if (statusFilter.some((v) => v && v !== "all")) initial.push("status");
    if (typeFilter.some((v) => v && v !== "all")) initial.push("type");
    return initial;
  });
  const [openFilterKey, setOpenFilterKey] = useState<DocFilterKey | null>(null);
  const ignoreAutoCloseRef = useRef<DocFilterKey | null>(null);

  const addFilter = useCallback((key: string) => {
    const fk = key as DocFilterKey;
    if (activeFilters.includes(fk)) return;
    setActiveFilters((prev) => [...prev, fk]);
    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);
      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) ignoreAutoCloseRef.current = null;
      }, 250);
    }, 50);
  }, [activeFilters]);

  const removeFilter = useCallback((key: DocFilterKey) => {
    setActiveFilters((prev) => prev.filter((k) => k !== key));
    setOpenFilterKey((prev) => (prev === key ? null : prev));
    if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
    if (key === "verified") onVerifiedFilterChange("all");
    if (key === "status") onStatusFilterChange(["all"]);
    if (key === "type") onTypeFilterChange(["all"]);
  }, [onVerifiedFilterChange, onStatusFilterChange, onTypeFilterChange]);

  const getFilterValue = (key: DocFilterKey): string | string[] => {
    if (key === "verified") return verifiedFilter;
    if (key === "status") return statusFilter;
    if (key === "type") return typeFilter;
    return "all";
  };

  const hasAnyChipFilter = activeFilters.length > 0;

  const resetAllFilters = () => {
    setActiveFilters([]);
    setOpenFilterKey(null);
    ignoreAutoCloseRef.current = null;
    onVerifiedFilterChange("all");
    onStatusFilterChange(["all"]);
    onTypeFilterChange(["all"]);
  };

  const allCategories: (FilterCategory & { key: DocFilterKey })[] = [
    { key: "verified", label: "Verification", icon: <ShieldCheck className="h-3.5 w-3.5" /> },
    { key: "status", label: "Status", icon: <Filter className="h-3.5 w-3.5" /> },
    { key: "type", label: "Type", icon: <Tag className="h-3.5 w-3.5" /> },
  ];

  const availableCategories = allCategories
    .filter((c) => !activeFilters.includes(c.key))
    .map(({ key, label, icon }) => ({ key, label, icon }));

  const optionsMap: Record<DocFilterKey, { label: string; options: ChipFilterOption[]; value: string | string[]; onChange: (v: string | string[]) => void; multiSelect?: boolean }> = {
    verified: { label: "Verification", options: VERIFIED_OPTIONS, value: verifiedFilter, onChange: (v) => onVerifiedFilterChange(Array.isArray(v) ? (v[0] ?? "all") : v) },
    status: { label: "Status", options: DOC_STATUS_OPTIONS, value: statusFilter, onChange: (v) => onStatusFilterChange(Array.isArray(v) ? v : [v]), multiSelect: true },
    type: { label: "Type", options: DOC_TYPE_OPTIONS, value: typeFilter, onChange: (v) => onTypeFilterChange(Array.isArray(v) ? v : [v]), multiSelect: true },
  };

  const renderChip = (key: DocFilterKey) => {
    const cfg = optionsMap[key];
    const categoryIcon = allCategories.find((c) => c.key === key)?.icon;
    return (
      <ChipFilter
        key={key}
        label={cfg.label}
        icon={categoryIcon}
        options={cfg.options}
        value={cfg.value}
        onChange={cfg.onChange}
        onRemove={() => removeFilter(key)}
        multiSelect={cfg.multiSelect}
        open={openFilterKey === key}
        onOpenChange={(isOpen) => {
          if (!isOpen && ignoreAutoCloseRef.current === key) {
            ignoreAutoCloseRef.current = null;
            return;
          }
          if (!isOpen) {
            const v = getFilterValue(key);
            const cleared = Array.isArray(v) ? v.length === 0 || v.every((x) => x === "all") : v === "all";
            if (cleared) {
              setActiveFilters((prev) => prev.filter((k) => k !== key));
            }
            setOpenFilterKey((prev) => (prev === key ? null : prev));
          } else {
            setOpenFilterKey(key);
          }
        }}
      />
    );
  };

  return (
    <>
      {activeFilters.map((key) => renderChip(key))}
      <AddFilterMenu
        categories={availableCategories}
        onSelect={addFilter}
        iconOnly={hasAnyChipFilter}
      />
      {hasAnyChipFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={resetAllFilters}
        >
          Reset filters
        </Button>
      )}
    </>
  );
}